/**
 * @file routes/_app/settings/clients.tsx
 * @description 客戶（組織）管理頁面。
 *
 * 設計（2026-06-27 客戶/派案欄位解耦後）：
 * - 客戶只承載「主體基礎資料」：名稱 / 統編 / 組織代碼 / 法人 / 負責人 / 聯絡人 /
 *   地址 / 驗證場所 / 採收後處理場所 / 客戶備註。
 * - 驗證業務屬性（驗證方案 / 類型 / 類別 / 合約來源 / 驗證資格 / 驗證選項 / 輔導員 /
 *   通路）不再掛在客戶上，改於「新建派案」（建立案件）時採集。
 *
 * 功能：
 * - 列表查詢（clientCode / name / verificationStatus）；列表額外顯示
 *   驗證方案 / 聯絡人 / 聯絡電話 / 聯絡人 Email / 通訊地址（客戶 PPT S7），
 *   聯絡欄位由後端列表端點 join 主聯絡人帶出，各列支持前端排序。
 * - 新增客戶（clientCode 由後端自動生成）
 * - 編輯客戶基本資料
 * - 軟刪除
 *
 * 視覺：ListPageShell + FilterBar + StatusPill
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Alert,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { message } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import {
  Plus,
  Search,
  Edit3,
  Users,
  CheckCircle2,
  Clock,
  RefreshCw,
  Trash2,
  ShieldAlert,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SCHEME_LABELS,
  VERIFICATION_SCHEMES,
  VERIFICATION_STATUSES,
  userCan,
  type ChangeClientVerificationStatusInput,
} from '@zhcb/shared';
import { useVerificationQualifications } from '@/hooks/useClientLookups';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import {
  ListPageShell,
  PageMetaItem,
  FilterBar,
  FilterBarLabel,
  StatusPill,
  type StatusPillProps,
} from '@/components/shell';
import { color, font, text, weight } from '@/styles/tokens';

/**
 * URL search params 結構：
 * - `action=create`：頁面載入後自動開啟新增 Drawer，並把 query 從 URL 移除（避免重複觸發）。
 * - `q=<keyword>`：作為搜尋初值（支援從 Command Palette 跳轉時帶關鍵字）。
 */
const clientsSearchSchema = z.object({
  action: z.enum(['create']).optional(),
  q: z.string().optional(),
});

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function optionalCreateAction(value: unknown): 'create' | undefined {
  return value === 'create' ? value : undefined;
}

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/clients')({
  component: ClientsPage,
  validateSearch: clientsSearchSchema,
});

/**
 * 客戶實體類型（對應後端 clients 表精簡視圖）。
 * 驗證業務屬性已移出客戶，不再出現於此。
 */
interface Client {
  id: number;
  clientCode: string;
  name: string;
  /** 綁定的驗證方案（TOP / TAP / ECO）；決定 clientCode 前綴，編輯態唯讀回填 */
  scheme?: string | null;
  taxId: string | null;
  /** 組織代碼（TAP 專用；身份類編碼，留在客戶） */
  orgCode?: string | null;
  verificationStatus: string;
  verificationStatusReason?: string | null;
  verificationStatusChangedAt?: string | null;
  // —— 法人（含身份證號） ——
  representativeName?: string | null;
  representativeIdNo?: string | null;
  representativeMobile?: string | null;
  representativeEmail?: string | null;
  // —— 負責人（無身份證號） ——
  personInChargeName?: string | null;
  personInChargePhone?: string | null;
  personInChargeEmail?: string | null;
  // —— 主聯絡人（編輯態由 GET /:id 帶回回填） ——
  primaryContact?: { name?: string | null; phone?: string | null; email?: string | null } | null;
  // —— 代表聯絡人展平欄位（列表端點 join client_contacts 帶出；isPrimary 優先） ——
  /** 聯絡人姓名 */
  contactName?: string | null;
  /** 聯絡電話（phone 優先，空缺回落 mobile） */
  contactPhone?: string | null;
  /** 聯絡人 Email */
  contactEmail?: string | null;
  registeredAddress?: string | null;
  contactAddress?: string | null;
  mailingAddress?: string | null;
  postalCode?: string | null;
  clientNotes?: string | null;
}

/** 後端分頁回應結構 */
interface ClientListResponse {
  items: Client[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** 各驗證狀態的全量家數（不受目前狀態篩選影響），供統計卡使用 */
  statusCounts: Record<string, number>;
}

/** 客戶驗證資格異動歷程 DTO。 */
interface VerificationLifecycleLog {
  id: number;
  fromStatus: string;
  toStatus: string;
  action: string;
  reason: string;
  actorId: number | null;
  actorName: string | null;
  createdAt: string;
}

/**
 * 驗證狀態 → StatusPill tone。
 * key 必須對齊後端 `clients.verificationStatus` 的實際枚舉（VERIFICATION_STATUSES，中文值），
 * 而非自定義的英文代碼，否則狀態下拉過濾與列表色彩都會失效。
 */
const verStatusToneMap: Record<string, { label: string; tone: StatusPillProps['tone'] }> = {
  新申請: { label: '新申請', tone: 'info' },
  維持: { label: '維持', tone: 'success' },
  結束驗證: { label: '結束驗證', tone: 'muted' },
  撤銷驗證: { label: '撤銷驗證', tone: 'danger' },
  暫時終止驗證_TOP: { label: '暫時終止驗證（TOP）', tone: 'warning' },
  暫時停止驗證_TAP: { label: '暫時停止驗證（TAP）', tone: 'warning' },
  恢復驗證: { label: '恢復驗證', tone: 'success' },
  終止驗證: { label: '終止驗證', tone: 'danger' },
} satisfies Record<
  (typeof VERIFICATION_STATUSES)[number],
  { label: string; tone: StatusPillProps['tone'] }
>;

/** 客戶管理頁面 */
function ClientsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const searchAction = optionalCreateAction(search.action);
  const searchQuery = optionalString(search.q);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [searchText, setSearchText] = useState<string>(() => searchQuery ?? '');
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterScheme, setFilterScheme] = useState<string | undefined>();
  // 伺服器端排序狀態（治「排序有 bug」：舊版前端 sorter 只排當頁，見下方 GET 查詢註解）
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  // 伺服器端分頁狀態（後端 pageSize 預設 20、上限 100；不帶參數只會回第一頁，故必須顯式傳遞）
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();
  const [lifecycleForm] = Form.useForm<ChangeClientVerificationStatusInput>();
  const [lifecycleClient, setLifecycleClient] = useState<Client | null>(null);
  const actor = useAuthStore((state) => state.user);
  const canManageLifecycle = !!actor && userCan(actor, 'client.manage');

  /**
   * 處理 URL search params 觸發的副作用：
   * - `?action=create`：自動開啟新增 Drawer，並 replace 掉 query 避免重整時重複觸發。
   * 注意：只在 search.action 變化時觸發，避免無限循環。
   */
  useEffect(() => {
    if (searchAction === 'create') {
      setEditingClient(null);
      form.resetFields();
      setModalOpen(true);
      void navigate({ to: '/settings/clients', search: { q: searchQuery }, replace: true });
    }
  }, [searchAction, searchQuery, form, navigate]);

  /** 查詢客戶列表（伺服器端分頁 + 排序：page/pageSize/sortBy/sortOrder 隨狀態變化重新拉取） */
  const { data, isLoading } = useQuery({
    queryKey: [
      'settings-clients',
      searchText,
      filterStatus,
      filterScheme,
      sortBy,
      sortOrder,
      page,
      pageSize,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      if (filterStatus) params.set('verificationStatus', filterStatus);
      if (filterScheme) params.set('scheme', filterScheme);
      if (sortBy) {
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);
      }
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      return apiClient.get<ClientListResponse>(`/api/settings/clients?${params.toString()}`);
    },
  });

  /** 僅在開啟狀態處置視窗時載入歷程，避免列表頁逐列查詢。 */
  const lifecycleHistory = useQuery({
    queryKey: ['client-verification-lifecycle', lifecycleClient?.id],
    queryFn: () =>
      apiClient.get<{ items: VerificationLifecycleLog[] }>(
        `/api/settings/clients/${lifecycleClient!.id}/verification-lifecycle`,
      ),
    enabled: lifecycleClient !== null,
  });

  /** 提交狀態異動；後端會原子同步案件凍結／結案、證書及名錄。 */
  const lifecycleMutation = useMutation({
    mutationFn: (input: ChangeClientVerificationStatusInput) =>
      apiClient.post(`/api/settings/clients/${lifecycleClient!.id}/verification-lifecycle`, input),
    onSuccess: () => {
      message.success('驗證狀態已更新，相關案件已同步處理');
      setLifecycleClient(null);
      lifecycleForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['settings-clients'] });
      void qc.invalidateQueries({ queryKey: ['client-verification-lifecycle'] });
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // 統計口徑對齊真實枚舉值（VERIFICATION_STATUSES）：維持＝驗證有效，新申請＝待處理
  // 全量家數（後端 GROUP BY 統計，不受伺服器端分頁截斷影響，亦不受目前狀態篩選影響）
  const activeCount = data?.statusCounts?.['維持'] ?? 0;
  const pendingCount = data?.statusCounts?.['新申請'] ?? 0;

  /** 開啟新增表單 */
  const handleAdd = () => {
    setEditingClient(null);
    form.resetFields();
    setModalOpen(true);
  };

  /** 開啟編輯表單 */
  const handleEdit = (record: Client) => {
    setEditingClient(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  /** 開啟驗證資格生命週期處置視窗。 */
  const handleLifecycle = (record: Client) => {
    lifecycleForm.resetFields();
    setLifecycleClient(record);
  };

  /** 依目前狀態提供合法動作；終態保留在名錄中但不可再次異動。 */
  const lifecycleActionOptions = (() => {
    const status = lifecycleClient?.verificationStatus;
    if (!status || ['結束驗證', '撤銷驗證', '終止驗證'].includes(status)) return [];
    if (['暫時終止驗證_TOP', '暫時停止驗證_TAP'].includes(status)) {
      return [
        { value: 'resume', label: '調查結束，恢復驗證' },
        { value: 'revoke', label: '確認違規，撤銷驗證' },
        { value: 'terminate', label: '確認違規，終止驗證' },
        { value: 'end', label: '其他原因，結束驗證' },
      ];
    }
    return [
      { value: 'pause', label: '緊急暫停（調查中）' },
      { value: 'revoke', label: '撤銷驗證' },
      { value: 'terminate', label: '終止驗證' },
      { value: 'end', label: '自願／證書期滿／死亡等結束驗證' },
    ];
  })();

  /**
   * 欄位的伺服器端排序 props 工廠：`sorter: true` 交給後端 ORDER BY（見 GET 查詢註解——
   * 舊版用前端比較函式排序，在伺服器端分頁下只排得動當頁 pageSize 筆，換頁即與整體順序
   * 脫節），並依目前 sortBy/sortOrder 狀態回顯表頭排序箭頭。
   */
  const serverSort = (key: string): { sorter: true; sortOrder: 'ascend' | 'descend' | null } => ({
    sorter: true,
    sortOrder: sortBy === key ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
  });

  /** 空值占位（統一的「—」muted 樣式） */
  const emptyCell = <span style={{ color: color.text.muted }}>—</span>;

  /**
   * 表格欄位（客戶 PPT S7 列順序）：
   * 驗證註冊碼 → 客戶名稱 → 驗證方案 → 聯絡人 → 聯絡電話 → 聯絡人 Email → 通訊地址 → 驗證狀態。
   * 統一編號（taxId）收窄為客戶名稱下方的次要小字，避免總寬度超出。
   */
  const columns: ColumnsType<Client> = [
    {
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 140,
      ...serverSort('clientCode'),
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.text.secondary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: '客戶名稱',
      dataIndex: 'name',
      width: 200,
      ...serverSort('name'),
      // 統一編號合併為名稱下的次要資訊（低優先列收窄，控制表格總寬）
      render: (v: string, record) => (
        <div>
          <div style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</div>
          {record.taxId && (
            <div style={{ fontFamily: font.mono, fontSize: text.xs, color: color.text.tertiary }}>
              統編 {record.taxId}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '驗證方案',
      dataIndex: 'scheme',
      width: 100,
      ...serverSort('scheme'),
      render: (v: string | null) =>
        v ? (SCHEME_LABELS[v as keyof typeof SCHEME_LABELS] ?? v) : emptyCell,
    },
    {
      title: '聯絡人',
      dataIndex: 'contactName',
      width: 100,
      ...serverSort('contactName'),
      render: (v: string | null) => v || emptyCell,
    },
    {
      title: '聯絡電話',
      dataIndex: 'contactPhone',
      width: 130,
      ...serverSort('contactPhone'),
      render: (v: string | null) =>
        v ? (
          <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
            {v}
          </span>
        ) : (
          emptyCell
        ),
    },
    {
      title: '聯絡人 Email',
      dataIndex: 'contactEmail',
      width: 180,
      ...serverSort('contactEmail'),
      render: (v: string | null) =>
        v ? (
          <Typography.Text
            style={{ fontSize: text.sm, color: color.text.secondary, maxWidth: 164 }}
            ellipsis={{ tooltip: v }}
          >
            {v}
          </Typography.Text>
        ) : (
          emptyCell
        ),
    },
    {
      title: '通訊地址',
      dataIndex: 'contactAddress',
      width: 220,
      ...serverSort('contactAddress'),
      render: (v: string | null) =>
        v ? (
          <Typography.Text
            style={{ fontSize: text.sm, color: color.text.secondary, maxWidth: 204 }}
            ellipsis={{ tooltip: v }}
          >
            {v}
          </Typography.Text>
        ) : (
          emptyCell
        ),
    },
    {
      title: '法人',
      dataIndex: 'representativeName',
      width: 90,
      ...serverSort('representativeName'),
      render: (v: string | null) => v || emptyCell,
    },
    {
      title: '驗證狀態',
      dataIndex: 'verificationStatus',
      width: 110,
      ...serverSort('verificationStatus'),
      render: (v: string) => {
        const display = verStatusToneMap[v];
        return display ? (
          <StatusPill tone={display.tone} size="sm" dot>
            {display.label}
          </StatusPill>
        ) : (
          <StatusPill tone="neutral" size="sm">
            {v}
          </StatusPill>
        );
      },
    },
    {
      title: '操作',
      key: '_actions',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="編輯">
            <Button
              type="text"
              size="small"
              icon={<Edit3 size={14} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {canManageLifecycle && (
            <Tooltip title="驗證狀態處置">
              <Button
                type="text"
                size="small"
                icon={<ShieldAlert size={14} />}
                disabled={['結束驗證', '撤銷驗證', '終止驗證'].includes(record.verificationStatus)}
                onClick={() => handleLifecycle(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="客戶管理"
        description="管理所有客戶的驗證註冊碼與主體基礎資料。驗證方案 / 類型等業務屬性於派案時設定。驗證註冊碼可留空自動接續編號，亦可手動填寫或修改。"
        meta={
          <>
            <PageMetaItem icon={<Users size={13} />} label="客戶總數" value={`${total} 家`} />
            {/* 2026-09-01 回報序1：說明「維持中」判定口徑。存量客戶匯入（序97 import-legacy-clients）
                時 Excel「驗證狀態」欄留空者預設為「維持」，故存量客戶可能大量顯示維持中，
                需由管理組逐一以「驗證狀態處置」或重新匯入含驗證狀態的底稿校正。 */}
            <Tooltip title="統計「驗證狀態＝維持」的客戶家數（全量，不受目前頁影響）。存量客戶匯入時若底稿未填驗證狀態會預設為「維持」，實際已結束／終止驗證的客戶請在操作欄「驗證狀態處置」更正。">
              <span>
                <PageMetaItem
                  icon={<CheckCircle2 size={13} />}
                  label="維持中"
                  value={`${activeCount} 家`}
                />
              </span>
            </Tooltip>
            <PageMetaItem icon={<Clock size={13} />} label="新申請" value={`${pendingCount} 家`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增客戶
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋名稱 / 驗證註冊碼"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
                allowClear
              />
            }
            filters={
              <>
                <FilterBarLabel>狀態</FilterBarLabel>
                <Select
                  placeholder="驗證狀態"
                  value={filterStatus}
                  onChange={(v) => {
                    setFilterStatus(v);
                    setPage(1);
                  }}
                  allowClear
                  style={{ width: 140 }}
                  options={Object.entries(verStatusToneMap).map(([v, d]) => ({
                    value: v,
                    label: d.label,
                  }))}
                />
                <FilterBarLabel>驗證方案</FilterBarLabel>
                <Select
                  placeholder="驗證方案"
                  value={filterScheme}
                  onChange={(v) => {
                    setFilterScheme(v);
                    setPage(1);
                  }}
                  allowClear
                  style={{ width: 140 }}
                  options={VERIFICATION_SCHEMES.map((s) => ({
                    value: s,
                    label: SCHEME_LABELS[s],
                  }))}
                />
              </>
            }
            extra={
              (searchText || filterStatus || filterScheme) && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => {
                    setSearchText('');
                    setFilterStatus(undefined);
                    setFilterScheme(undefined);
                    setPage(1);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<Client>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 1340 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 筆資料`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          // 表頭點擊排序：下推 sortBy/sortOrder 給後端 ORDER BY（見上方查詢與 serverSort 註解）
          // ⚠️ antd Table 的 onChange 在「翻頁 / 改每頁筆數」時同樣會觸發（action='paginate'），
          // 舊版不分 action 一律 setPage(1)，導致點下一頁後又被重置回第 1 頁＝「無法翻頁」
          // （尤其在驗證註冊碼降序後最明顯）。故僅在 action==='sort' 時處理排序並回到第 1 頁，
          // 翻頁交由上方 pagination.onChange 處理。
          onChange={(_pagination, _filters, sorter, extra) => {
            if (extra.action !== 'sort') return;
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const field = s?.field as string | undefined;
            if (!field || !s?.order) {
              setSortBy(undefined);
            } else {
              setSortBy(field);
              setSortOrder(s.order === 'ascend' ? 'asc' : 'desc');
            }
            setPage(1);
          }}
        />
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editingClient ? '編輯客戶' : '新增客戶'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <ClientForm
          item={editingClient}
          onClose={() => {
            setModalOpen(false);
            form.resetFields();
            void qc.invalidateQueries({ queryKey: ['settings-clients'] });
          }}
          form={form}
        />
      </Modal>

      {/* 客戶驗證資格生命週期：暫停調查、恢復、撤銷／終止；客戶主檔永不刪除。 */}
      <Modal
        title={`驗證狀態處置${lifecycleClient ? `－${lifecycleClient.name}` : ''}`}
        open={lifecycleClient !== null}
        onCancel={() => {
          setLifecycleClient(null);
          lifecycleForm.resetFields();
        }}
        onOk={() => lifecycleForm.submit()}
        okText="確認處置"
        confirmLoading={lifecycleMutation.isPending}
        okButtonProps={{ danger: lifecycleForm.getFieldValue('action') !== 'resume' }}
        width={620}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          message="客戶資料與異動歷程會永久保留"
          description="暫停後所有未結案件會凍結且不得推進；恢復後從原進度續辦。撤銷、終止或結束驗證會將未結案件結案，並同步證書與獲證名錄狀態。"
          style={{ marginBottom: 16 }}
        />
        <Form
          form={lifecycleForm}
          layout="vertical"
          onFinish={(values) => lifecycleMutation.mutate(values)}
        >
          <Form.Item label="目前狀態">
            <Typography.Text strong>
              {lifecycleClient
                ? (verStatusToneMap[lifecycleClient.verificationStatus]?.label ??
                  lifecycleClient.verificationStatus)
                : '—'}
            </Typography.Text>
          </Form.Item>
          <Form.Item
            name="action"
            label="處置"
            rules={[{ required: true, message: '請選擇處置方式' }]}
          >
            <Select placeholder="請選擇處置方式" options={lifecycleActionOptions} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="原因"
            rules={[
              { required: true, message: '請填寫異動原因' },
              { min: 2, max: 1000 },
            ]}
          >
            <Input.TextArea
              rows={4}
              maxLength={1000}
              showCount
              placeholder="請記錄市場重大違規、調查結果、自願終止、證書期滿或其他具體原因"
            />
          </Form.Item>
        </Form>

        <Typography.Title level={5} style={{ marginTop: 20 }}>
          歷次異動
        </Typography.Title>
        {lifecycleHistory.isLoading ? (
          <Typography.Text type="secondary">載入中…</Typography.Text>
        ) : (lifecycleHistory.data?.items.length ?? 0) === 0 ? (
          <Typography.Text type="secondary">尚無異動紀錄</Typography.Text>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {lifecycleHistory.data?.items.map((item) => (
              <div
                key={item.id}
                style={{
                  border: `1px solid ${color.border.subtle}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              >
                <Typography.Text strong>
                  {verStatusToneMap[item.fromStatus]?.label ?? item.fromStatus}
                  {' → '}
                  {verStatusToneMap[item.toStatus]?.label ?? item.toStatus}
                </Typography.Text>
                <div style={{ color: color.text.secondary, marginTop: 4 }}>{item.reason}</div>
                <div style={{ color: color.text.muted, fontSize: text.xs, marginTop: 4 }}>
                  {item.actorName ?? `使用者 #${item.actorId ?? '—'}`} ·{' '}
                  {new Date(item.createdAt).toLocaleString('zh-TW')}
                </div>
              </div>
            ))}
          </Space>
        )}
      </Modal>
    </>
  );
}

/** 區塊小標題：分隔表單各邏輯分組（左側品牌色竪條 + 加粗標題，取代貫穿橫線） */
function SectionTitle({ children, first }: { children: React.ReactNode; first?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        // 首個分組標題緊貼 Modal 標題，避免頂部出現大段空白
        margin: first ? '0 0 16px' : '20px 0 16px',
      }}
    >
      {/* 左側品牌色竪條，作為分組視覺錨點 */}
      <span
        style={{
          display: 'inline-block',
          width: 3,
          height: 14,
          borderRadius: 2,
          background: color.brand.primary,
        }}
      />
      <Typography.Text
        strong
        style={{ fontSize: text.md, fontWeight: weight.semibold, color: color.text.primary }}
      >
        {children}
      </Typography.Text>
    </div>
  );
}

/** 場所地址表單列（序33：編輯時帶既有 id；新增列無 id） */
interface ClientSiteFormRow {
  id?: number;
  address?: string | null;
}

/**
 * 把場所表單列轉為 PATCH payload（序33）。
 * 保留 id（更新/刪除判定用）與去頭尾空白的地址；無 id 且地址為空的占位列直接略過。
 *
 * @param rows 表單中的場所清單
 * @returns 後端 `clientSiteUpsertInputSchema` 陣列
 */
function toSitePayload(rows: ClientSiteFormRow[] | undefined): ClientSiteFormRow[] {
  return (rows ?? [])
    .map((r) => ({
      ...(r?.id ? { id: Number(r.id) } : {}),
      address: r?.address?.trim() ?? '',
    }))
    .filter((r) => r.id !== undefined || r.address !== '');
}

/**
 * 客戶新增/編輯表單（僅主體基礎資料）。
 *
 * 新增：收集主體資料 + 主聯絡人 / 驗證場所 / 採收後處理場所子表，一次性建檔。
 * 編輯：僅更新 clients 主表標量欄位 + 就地 upsert 主聯絡人；場所子表的增刪改列為後續（詳情頁），
 *      故編輯時隱藏場所子表區塊。
 */
function ClientForm({
  item,
  onClose,
  form,
}: {
  item: Client | null;
  onClose: () => void;
  form: ReturnType<typeof Form.useForm>[0];
}) {
  const qc = useQueryClient();
  const isEdit = !!item;

  // 驗證資格字典（序102：驗證資格於客戶檔案維護），僅取啟用項
  const qualificationsQuery = useVerificationQualifications({ activeOnly: true });

  /**
   * 「負責人與法人相同」勾選狀態 + 法人三欄監聽：
   * 勾選後把法人 姓名/電話/Email 同步進負責人欄位，並 disable 負責人輸入。
   * `personInChargeSameAsLegal` 為純 UI 臨時欄位，不提交後端。
   */
  const sameAsLegal = Form.useWatch('personInChargeSameAsLegal', form);
  const repName = Form.useWatch('representativeName', form);
  const repMobile = Form.useWatch('representativeMobile', form);
  const repEmail = Form.useWatch('representativeEmail', form);
  useEffect(() => {
    if (sameAsLegal) {
      form.setFieldsValue({
        personInChargeName: repName,
        personInChargePhone: repMobile,
        personInChargeEmail: repEmail,
      });
    }
  }, [sameAsLegal, repName, repMobile, repEmail, form]);

  /**
   * 「通訊地址與戶籍地址相同」勾選（序98）：
   * 勾選後把戶籍地址同步進通訊地址並 disable 輸入。
   * `contactSameAsRegistered` 為純 UI 臨時欄位，不提交後端。
   */
  const contactSameAsRegistered = Form.useWatch('contactSameAsRegistered', form);
  const registeredAddr = Form.useWatch('registeredAddress', form);
  useEffect(() => {
    if (contactSameAsRegistered) {
      form.setFieldsValue({ contactAddress: registeredAddr });
    }
  }, [contactSameAsRegistered, registeredAddr, form]);

  /**
   * 編輯模式下，拉單筆客戶詳情以回填主聯絡人與場所地址（列表 record 不含子表）。
   * 取得後寫入 primaryContact 三欄；序33（2026-09-25）另回填 lands / processingSites（含 id）。
   */
  const detailQuery = useQuery({
    queryKey: ['settings-client-detail', item?.id],
    queryFn: () =>
      apiClient.get<{
        item: {
          primaryContact: { name?: string; phone?: string; email?: string } | null;
          lands?: ClientSiteFormRow[];
          processingSites?: ClientSiteFormRow[];
        } | null;
      }>(`/api/settings/clients/${item!.id}`),
    enabled: isEdit,
  });
  /**
   * 序33：場所清單是否已從詳情成功回填。
   * 未回填前提交不可送出場所清單——後端以「清單即最終狀態」同步，送空清單會誤刪既有場所。
   */
  const sitesLoaded = isEdit && detailQuery.isSuccess && !!detailQuery.data?.item;
  useEffect(() => {
    const detail = detailQuery.data?.item;
    if (!detail) return;
    const pc = detail.primaryContact;
    if (pc) {
      form.setFieldsValue({
        primaryContact: { name: pc.name, phone: pc.phone, email: pc.email },
      });
    }
    form.setFieldsValue({
      lands: (detail.lands ?? []).map((l) => ({ id: l.id, address: l.address })),
      processingSites: (detail.processingSites ?? []).map((ps) => ({
        id: ps.id,
        address: ps.address,
      })),
    });
  }, [detailQuery.data, form]);

  /** 新增客戶 Mutation（POST 端點自動生成 clientCode） */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/settings/clients', v),
    onSuccess: () => {
      message.success('客戶已建立');
      void qc.invalidateQueries({ queryKey: ['settings-clients'] });
      onClose();
    },
    // 序25：驗證註冊碼重複 / 前綴不符等業務錯誤需明確提示
    onError: (err: Error) => {
      console.error('[settings/clients] create client failed', err);
      message.error(err.message || '建立客戶失敗');
    },
  });

  /** 更新客戶 Mutation */
  const update = useMutation({
    mutationFn: (v: unknown) => apiClient.patch(`/api/settings/clients/${item!.id}`, v),
    onSuccess: () => {
      message.success('客戶資料已更新');
      void qc.invalidateQueries({ queryKey: ['settings-clients'] });
      // 序33：場所地址可能已變更，下次開啟編輯需重新拉取詳情
      void qc.invalidateQueries({ queryKey: ['settings-client-detail', item?.id] });
      onClose();
    },
    onError: (err: Error) => {
      console.error('[settings/clients] update client failed', err);
      message.error(err.message || '更新客戶失敗');
    },
  });

  /**
   * 提交前組裝 payload：
   * - 剝離純 UI 欄位 personInChargeSameAsLegal（不入庫）。
   * - 新增：主聯絡人有姓名才轉成 contacts；場所過濾占位列。
   * - 編輯：主聯絡人以 primaryContact 傳給 PATCH 做就地 upsert。
   */
  const handleFinish = (raw: unknown) => {
    const v = raw as {
      primaryContact?: { name?: string; phone?: string; email?: string };
      lands?: ClientSiteFormRow[];
      processingSites?: ClientSiteFormRow[];
      personInChargeSameAsLegal?: boolean;
      [k: string]: unknown;
    };
    // primaryContact 從標量欄位中剝離；personInChargeSameAsLegal 為純 UI 欄位，於下方移除
    const { primaryContact, ...rest } = v;

    const payload: Record<string, unknown> = { ...rest };
    // 驗證註冊碼：新增時留空 → 不送（後端自動編號）；編輯時未變更 → 不送（避免多餘校驗）
    const code = typeof payload.clientCode === 'string' ? payload.clientCode.trim() : '';
    if (!code || (isEdit && code === item?.clientCode)) delete payload.clientCode;
    else payload.clientCode = code;
    // 純 UI 欄位不提交後端
    delete payload.personInChargeSameAsLegal;
    delete payload.contactSameAsRegistered;

    if (!isEdit) {
      // 主聯絡人（僅在有姓名時轉成 contacts 陣列）
      const contactName = primaryContact?.name?.trim();
      payload.contacts = contactName
        ? [
            {
              name: primaryContact?.name,
              phone: primaryContact?.phone,
              email: primaryContact?.email,
              isPrimary: true,
            },
          ]
        : undefined;
      // 驗證場所 / 採收後處理場所（需有地址）
      payload.lands = (v.lands ?? []).filter((l) => l?.address?.trim());
      payload.processingSites = (v.processingSites ?? []).filter((s) => s?.address?.trim());
    } else {
      // 編輯：主聯絡人交給 PATCH upsert（含清空語義：name 為空則刪除既有主聯絡人）
      payload.primaryContact = primaryContact
        ? {
            name: primaryContact.name,
            phone: primaryContact.phone,
            email: primaryContact.email,
          }
        : undefined;
      // 序33：場所地址以全量清單送出（帶 id 更新、無 id 新增、移除/清空者由後端軟刪除）；
      // 詳情未回填成功時不送，避免空清單誤刪既有場所
      if (sitesLoaded) {
        payload.lands = toSitePayload(v.lands);
        payload.processingSites = toSitePayload(v.processingSites);
      } else {
        delete payload.lands;
        delete payload.processingSites;
      }
    }

    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={item ?? {}}
      onFinish={handleFinish}
      style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}
    >
      <SectionTitle first>基本資料</SectionTitle>

      {/* 客戶名稱（全寬） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="name" label="客戶名稱" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* 驗證方案（決定驗證註冊碼前綴；建立後不可變更，編輯時唯讀） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="scheme"
            label="驗證方案"
            rules={[{ required: true, message: '請選擇驗證方案' }]}
            tooltip="客戶綁定的驗證方案，決定驗證註冊碼前綴（TOP→CAS、TAP→TAP、ECO→ECO），例如 ZH-CAS-0001。建立後不可變更。"
          >
            <Select
              placeholder="請選擇驗證方案"
              disabled={isEdit}
              options={VERIFICATION_SCHEMES.map((s) => ({ value: s, label: SCHEME_LABELS[s] }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 驗證註冊碼（2026-09-15 回報序25）：新增時選填（留空自動接續編號），編輯時可修改 */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="clientCode"
            label="驗證註冊碼"
            normalize={(v?: string) => v?.toUpperCase()}
            rules={[
              {
                pattern: /^ZH-[A-Z]{2,4}-?\d{4,}$/,
                message: '格式須如 ZH-TAP1285（前綴需與驗證方案一致：TOP→CAS、TAP→TAP）',
              },
              ...(isEdit ? [{ required: true, message: '驗證註冊碼不可為空' }] : []),
            ]}
            extra={
              isEdit
                ? '修改後將套用於後續新建案件；既有案件編號與已發出的證書不會變更。'
                : '留空由系統依驗證方案自動接續編號；已有驗證註冊碼的客戶請直接填入（如 ZH-TAP1285）。'
            }
          >
            <Input placeholder={isEdit ? undefined : '留空自動編號'} maxLength={20} allowClear />
          </Form.Item>
        </Col>
      </Row>

      {/* 驗證資格（主體類型多選；序102 自「新建案件」遷移至客戶檔案，建案時自動帶入） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="verificationQualifications"
            label="驗證資格"
            tooltip="主體類型：自然人 / 農場 / 產銷班 / 學校 / 法人或團體 …；可複選。新建驗證案件時自動帶入本欄位。"
          >
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="請選擇驗證資格（主體類型，可複選）"
              loading={qualificationsQuery.isLoading}
              options={(qualificationsQuery.data?.items ?? []).map((x) => ({
                value: x.name,
                label: x.name,
              }))}
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 統一編號 + 組織代碼（TAP 專用） */}
      <Row gutter={16}>
        <Col span={12}>
          {/* 統一編號＝公司法定稅號（國稅局核發，非系統自動編號），選填 */}
          <Form.Item
            name="taxId"
            label="統一編號"
            tooltip="公司法定稅號（國稅局核發），非系統客戶編號，選填"
          >
            <Input maxLength={30} placeholder="公司 8 位統一編號，選填" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="orgCode" label="組織代碼" tooltip="TAP 產銷履歷專用組織代碼，選填">
            <Input maxLength={50} placeholder="TAP 組織代碼，選填" />
          </Form.Item>
        </Col>
      </Row>

      {/* —— 法人（法定代表人，含身份證號） —— */}
      <SectionTitle>法人</SectionTitle>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="representativeName" label="法人姓名">
            <Input maxLength={100} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="representativeIdNo" label="身份證號">
            <Input maxLength={50} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="representativeMobile" label="法人電話">
            <Input maxLength={30} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="representativeEmail"
            label="法人 Email"
            rules={[{ type: 'email', message: '請輸入有效的 Email' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* —— 負責人（無身份證號，可勾選與法人相同自動帶入） —— */}
      <SectionTitle>負責人</SectionTitle>
      <Form.Item
        name="personInChargeSameAsLegal"
        valuePropName="checked"
        style={{ marginBottom: 12 }}
      >
        <Checkbox>與法人相同（勾選後自動帶入法人資料）</Checkbox>
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="personInChargeName" label="負責人姓名">
            <Input maxLength={100} disabled={!!sameAsLegal} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="personInChargePhone" label="負責人電話">
            <Input maxLength={30} disabled={!!sameAsLegal} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="personInChargeEmail"
            label="負責人 Email"
            rules={[{ type: 'email', message: '請輸入有效的 Email' }]}
          >
            <Input disabled={!!sameAsLegal} />
          </Form.Item>
        </Col>
      </Row>

      {/* —— 聯絡人（單條主聯絡人，新增與編輯都可填） —— */}
      <SectionTitle>聯絡人</SectionTitle>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name={['primaryContact', 'name']} label="聯絡人姓名">
            <Input maxLength={100} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={['primaryContact', 'phone']} label="聯絡人電話">
            <Input maxLength={30} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['primaryContact', 'email']}
            label="聯絡人 Email"
            rules={[{ type: 'email', message: '請輸入有效的 Email' }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <SectionTitle>地址</SectionTitle>

      {/* 戶籍地址 + 通訊地址（序98：刪除寄件地址/郵遞區號；通訊地址可勾選與戶籍相同帶入） */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="registeredAddress" label="戶籍地址">
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="contactAddress" label="通訊地址">
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 3 }}
              disabled={!!contactSameAsRegistered}
            />
          </Form.Item>
          <Form.Item
            name="contactSameAsRegistered"
            valuePropName="checked"
            style={{ marginTop: -12, marginBottom: 12 }}
          >
            <Checkbox>與戶籍地址相同（勾選後自動帶入）</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      {/* 場所子表：新增時一次建檔；序33（2026-09-25）編輯時亦可新增／修改／移除 */}
      {(!isEdit || sitesLoaded) && (
        <>
          <SectionTitle>驗證場所地址</SectionTitle>
          <Form.List name="lands">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={22}>
                      {/* 既有場所的 id（隱藏），供後端就地更新 */}
                      <Form.Item name={[field.name, 'id']} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, 'address']} noStyle>
                        <Input placeholder="驗證場所地址" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={14} />}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" block icon={<Plus size={14} />} onClick={() => add()}>
                  新增驗證場所
                </Button>
              </>
            )}
          </Form.List>

          <SectionTitle>採收後處理場所地址</SectionTitle>
          <Form.List name="processingSites">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={22}>
                      {/* 既有場所的 id（隱藏），供後端就地更新 */}
                      <Form.Item name={[field.name, 'id']} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, 'address']} noStyle>
                        <Input placeholder="採收後處理場所地址" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={14} />}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" block icon={<Plus size={14} />} onClick={() => add()}>
                  新增處理場所
                </Button>
              </>
            )}
          </Form.List>
        </>
      )}

      <SectionTitle>備註</SectionTitle>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="clientNotes" label="客戶備註">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '建立'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
